import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { bulkBinAPI, FEED_FORMS } from '../../services/storeAPI';
import StoreHeader from '../../components/Layout/StoreHeader';

interface BulkBin {
  id: number;
  name: string;
  product_id: number | null;
  product_name: string;
  feed_form: string | null;
  qr_code: string;
  barcode: string | null;
  capacity_lbs: number;
  current_lbs: number;
  percentage_full: number;
  reorder_threshold_percent: number;
  reorder_quantity_lbs: number;
  needs_refill: boolean;
  last_filled: string | null;
  last_updated: string;
  notes: string;
  is_active?: boolean;
}

const StoreBulkBins: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const [bins, setBins] = useState<BulkBin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFillModal, setShowFillModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBin, setSelectedBin] = useState<BulkBin | null>(null);
  // Toast state — used by handleEdit (success + failure) and handleFill
  // (failure) instead of native alert(). Auto-clears after 4 seconds.
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const showToast = (kind: 'success' | 'error', message: string) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const binsData = await bulkBinAPI.getAll();
      setBins(binsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load bins');
    } finally {
      setLoading(false);
    }
  };

  const handleFill = async (amountLbs: number, notes: string) => {
    if (!selectedBin) return;
    try {
      await bulkBinAPI.fill(selectedBin.id, amountLbs, notes);
      setShowFillModal(false);
      setSelectedBin(null);
      loadData();
    } catch (err: any) {
      showToast('error', err.message || 'Failed to fill bin');
    }
  };

  const handleEdit = async (data: any) => {
    if (!selectedBin) return;
    const editedBinId = selectedBin.id;
    try {
      await bulkBinAPI.update(editedBinId, data);
      setShowEditModal(false);
      setSelectedBin(null);
      loadData();
      // Success was silent before — now visible so the user has
      // positive confirmation instead of "did it save?" anxiety.
      showToast('success', `✓ Bin #${editedBinId} updated`);
    } catch (err: any) {
      // Toast replaces the jarring native alert(). Same content,
      // less hostile.
      showToast('error', err.message || 'Failed to update bin');
    }
  };

  const getStatusColor = (percentage: number) => {
    if (percentage <= 20) return '#f44336';
    if (percentage <= 40) return '#ff9800';
    return '#4CAF50';
  };

  // Primary display label for the card. Reads bin.product_name directly
  // (same source the customer page uses) so staff and customer views
  // can never disagree on what a bin contains. Falls through to the bin's
  // own name if no product is linked, then to a clear sentinel if both
  // are missing. Backend returns 'Empty' when bin.product is None
  // (see store_views.bulk_bin_list); treat that as "no product set".
  const getDisplayName = (bin: BulkBin): string => {
    const productName = (bin.product_name || '').trim();
    if (productName && productName !== 'Empty') return productName;
    if (bin.name) return bin.name;
    return '(no product set)';
  };

  // Form descriptor for the pill. Step 3: read the structured BulkBin.feed_form
  // column; fall back to the legacy notes prefix ONLY for un-backfilled bins
  // (feed_form NULL) during the transition.
  const FORM_LABELS: Record<string, string> = {
    MASH: 'Mash', PELLET: 'Pellet', CRUMBLE: 'Crumble', WHOLE: 'Whole Grain', MIXED: 'Mixed',
  };
  const extractFormFromNotes = (notes: string): string => {
    const feedStr = (notes || '').split('|')[0].toLowerCase();
    if (feedStr.includes('pellet')) return 'Pellet';
    if (feedStr.includes('whole')) return 'Whole Grain';
    return 'Mash';
  };
  const formLabelFor = (bin: BulkBin): string =>
    bin.feed_form ? (FORM_LABELS[bin.feed_form] || bin.feed_form) : extractFormFromNotes(bin.notes || '');

  // DEPRECATED: previously the primary label source. Replaced by
  // getDisplayName (reads bin.product_name) on 2026-05-16 because the
  // FIVE_STAR_FEEDS substring match misfired ("Goat Chow" inside
  // "Dairy Goat Chow Mash" → wrong label "Goat Chow"). Kept only as a
  // reference for the form-extraction sibling. Will be removed when
  // notes structure is migrated to a structured form_type field on the
  // BulkBin model (see Phase 3 spec).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _parseFeedInfo_DEPRECATED = (bin: BulkBin) => ({
    feedName: getDisplayName(bin),
    form: extractFormFromNotes(bin.notes || ''),
    seasonal: null,
  });

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />
      <main style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>Bulk Bins (BYOC)</h1>
          <button style={styles.addButton} onClick={() => setShowAddModal(true)}>+ Add Bin</button>
        </div>

        {/* Toast — success or error from edit/fill ops. Replaces the
            native alert() pattern; auto-clears via showToast's timer. */}
        {toast && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 6,
              marginBottom: 16,
              background: toast.kind === 'success' ? '#ECFDF5' : '#FEF2F2',
              border: `1px solid ${toast.kind === 'success' ? '#10B981' : '#FCA5A5'}`,
              color: toast.kind === 'success' ? '#065F46' : '#991B1B',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {toast.message}
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>Loading bins...</div>
        ) : error ? (
          <div style={styles.error}>{error}</div>
        ) : (
          <div style={styles.binsGrid}>
            {bins.map(bin => {
              // Primary label = bin.product_name (same source as the
              // customer page) → staff + customer can't disagree.
              // Form pill (Mash / Pellet / Whole Grain) still parsed
              // from notes since notes is where the form info lives.
              const displayName = getDisplayName(bin);
              const formLabel = formLabelFor(bin);
              return (
                <div key={bin.id} style={{...styles.binCard, borderColor: bin.needs_refill ? '#f44336' : '#e0e0e0'}}>
                  <div style={styles.binHeader}>
                    <h3 style={styles.binName}>{bin.name}</h3>
                    <div style={styles.binHeaderActions}>
                      {bin.needs_refill && <span style={styles.refillBadge}>NEEDS REFILL</span>}
                      <button style={styles.editBtn} onClick={() => { setSelectedBin(bin); setShowEditModal(true); }} title="Edit Bin">Edit</button>
                    </div>
                  </div>
                  <div style={styles.feedDisplay}>
                    <div style={styles.feedName}>{displayName}</div>
                    <div style={styles.feedDetails}>
                      <span style={styles.feedForm}>{formLabel}</span>
                    </div>
                  </div>
                  <div style={styles.levelContainer}>
                    <div style={styles.levelBar}>
                      <div style={{...styles.levelFill, width: `${bin.percentage_full}%`, backgroundColor: getStatusColor(bin.percentage_full)}} />
                    </div>
                    <div style={styles.levelText}>{bin.percentage_full.toFixed(0)}% Full</div>
                  </div>
                  <div style={styles.binStats}>
                    <div style={styles.stat}><span style={styles.statLabel}>Current:</span><span style={styles.statValue}>{bin.current_lbs.toFixed(0)} lbs</span></div>
                    <div style={styles.stat}><span style={styles.statLabel}>Capacity:</span><span style={styles.statValue}>{bin.capacity_lbs} lbs</span></div>
                  </div>
                  <div style={styles.qrCode}>QR: {bin.qr_code}</div>
                  <div style={styles.binActions}>
                    <button style={styles.fillBtn} onClick={() => { setSelectedBin(bin); setShowFillModal(true); }}>Fill Bin</button>
                  </div>
                </div>
              );
            })}
            {bins.length === 0 && (
              <div style={styles.emptyState}>No bulk bins configured yet.<button style={styles.addButton} onClick={() => setShowAddModal(true)}>Add First Bin</button></div>
            )}
          </div>
        )}
      </main>
      {showFillModal && selectedBin && <FillBinModal bin={selectedBin} onClose={() => { setShowFillModal(false); setSelectedBin(null); }} onSubmit={handleFill} />}
      {showEditModal && selectedBin && <EditBinModal bin={selectedBin} onClose={() => { setShowEditModal(false); setSelectedBin(null); }} onSubmit={handleEdit} />}
      {showAddModal && <AddBinModal onClose={() => setShowAddModal(false)} onSubmit={async (data) => { try { await bulkBinAPI.create(data); setShowAddModal(false); loadData(); } catch (err: any) { alert(err.message || 'Failed to add bin'); } }} />}
    </div>
  );
};

const FillBinModal: React.FC<{bin: BulkBin; onClose: () => void; onSubmit: (amountLbs: number, notes: string) => void;}> = ({ bin, onClose, onSubmit }) => {
  const [amount, setAmount] = useState(bin.reorder_quantity_lbs);
  const [notes, setNotes] = useState('');
  const newLevel = Math.min(bin.current_lbs + amount, bin.capacity_lbs);
  const newPercentage = (newLevel / bin.capacity_lbs) * 100;
  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>Fill Bin</h2>
        <p style={styles.modalSubtitle}>{bin.name}</p>
        <div style={styles.currentLevel}>Current: {bin.current_lbs.toFixed(0)} / {bin.capacity_lbs} lbs ({bin.percentage_full.toFixed(0)}%)</div>
        <div style={styles.formGroup}><label style={styles.label}>Amount to Add (lbs)</label><input type="text" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value as any)} onBlur={(e) => { const val = parseFloat(e.target.value) || 0; setAmount(Math.min(Math.max(0, val), bin.capacity_lbs - bin.current_lbs)); }} style={styles.input} /><p style={styles.newLevel}>New Level: {newLevel.toFixed(0)} lbs ({newPercentage.toFixed(0)}%)</p></div>
        <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={styles.textarea} placeholder="Optional notes..." /></div>
        <div style={styles.modalActions}><button style={styles.cancelBtn} onClick={onClose}>Cancel</button><button style={styles.submitBtn} onClick={() => onSubmit(amount, notes)} disabled={amount <= 0}>Fill Bin</button></div>
      </div>
    </div>
  );
};

const EditBinModal: React.FC<{bin: BulkBin; onClose: () => void; onSubmit: (data: any) => void;}> = ({ bin, onClose, onSubmit }) => {
  const [name, setName] = useState(bin.name);
  const [selectedProductId, setSelectedProductId] = useState<number | ''>(bin.product_id || '');
  const [selectedForm, setSelectedForm] = useState('MASH');
  const [capacity, setCapacity] = useState(bin.capacity_lbs);
  const [currentLbs, setCurrentLbs] = useState(bin.current_lbs);
  const [threshold, setThreshold] = useState(bin.reorder_threshold_percent);
  const [isActive, setIsActive] = useState(bin.is_active !== false);
  const [userNotes, setUserNotes] = useState('');
  const [apiFormulas, setApiFormulas] = useState<{id: number; name: string}[]>([]);
  const [loadingFormulas, setLoadingFormulas] = useState(true);
  // Notes-vs-product divergence warning. Two-step confirmation so the
  // user can opt to either auto-update notes or save as-is — catches
  // the exact rot pattern that caused today's mess (notes says one
  // thing, FK says another).
  const [pendingDivergence, setPendingDivergence] = useState<{
    oldNotesLabel: string;
    newProductName: string;
  } | null>(null);

  useEffect(() => {
    const notes = bin.notes || '';
    const pipeIndex = notes.indexOf('|');
    const notePart = pipeIndex > 0 ? notes.substring(pipeIndex + 1) : '';
    setUserNotes(notePart);

    // Step 3: prefer the structured form column; fall back to the legacy notes
    // prefix only for un-backfilled bins (feed_form NULL).
    if (bin.feed_form) {
      setSelectedForm(bin.feed_form);
    } else {
      const formStr = (pipeIndex > 0 ? notes.substring(0, pipeIndex) : notes).toLowerCase();
      if (formStr.includes('pellet')) { setSelectedForm('PELLET'); }
      else if (formStr.includes('whole')) { setSelectedForm('WHOLE'); }
    }
  }, [bin.notes]);

  // Load ALL formulas (not just mill_only) so bins pointing at
  // non-mill formulas like "Cavalry Horse" or "Rabbit 18" can be
  // re-selected by the user. Previously the dropdown silently
  // excluded non-mill formulas, which (combined with the disabled-
  // submit guard) made bins pointing at non-mill formulas
  // un-editable.
  useEffect(() => {
    const loadFormulas = async () => {
      try {
        const { feedFormulasAPI } = await import('../../services/api');
        const { data } = await feedFormulasAPI.getAll();
        const list = data?.results || data || [];
        setApiFormulas(
          list.map((f: any) => ({ id: f.id, name: f.name }))
              .sort((a: {name: string}, b: {name: string}) => a.name.localeCompare(b.name))
        );
      } catch {
        // Formula API failed — show an empty dropdown rather than a
        // stale hardcoded list. Better to surface the load failure
        // than silently substitute outdated options. (FIVE_STAR_FEEDS
        // removed 2026-05-16; was the source of the parser-misfire bug.)
        setApiFormulas([]);
      } finally {
        setLoadingFormulas(false);
      }
    };
    loadFormulas();
  }, []);

  const getSelectedFeedName = () => {
    if (!selectedProductId) return '';
    const formula = apiFormulas.find(f => f.id === selectedProductId);
    return formula?.name || '';
  };

  // Sentinel rendering: when bin.product_id exists but isn't in the
  // loaded formula list (e.g., the FK points at an inactive or
  // recently-renamed formula), show what's currently linked so the
  // user can see the state instead of a blank dropdown. The sentinel
  // option is disabled — user must pick a real formula OR clear it.
  const currentNotInDropdown =
    !!bin.product_id
    && !apiFormulas.find(f => f.id === bin.product_id)
    && !loadingFormulas;

  const submitWithFeedName = (_feedName: string) => {
    onSubmit({
      name,
      product_id: selectedProductId || null,
      feed_form: selectedForm,        // Step 3: structured form key (not notes)
      capacity_lbs: capacity,
      current_lbs: currentLbs,
      reorder_threshold_percent: threshold,
      is_active: isActive,
      notes: userNotes,               // notes now hold ONLY user notes
    });
  };

  // Token-overlap divergence check between the old notes label
  // (what's been showing as the bin's "feed name" in notes) and
  // the newly-picked product. If the user picked a different
  // product but didn't touch notes, we offer to update both.
  const checkDivergenceAndSubmit = () => {
    const newFeedName = getSelectedFeedName();
    const oldNotes = bin.notes || '';
    const oldNotesLabel = oldNotes.split('|')[0].trim();
    // Strip form suffix for comparison
    const cleanLabel = oldNotesLabel
      .replace(/\s+(mash|pellet|whole grain)\s*$/i, '')
      .trim()
      .toLowerCase();
    const newName = newFeedName.toLowerCase().trim();

    // No new product picked OR no prior notes OR label already matches
    // → submit immediately, no warning.
    if (!newFeedName || !cleanLabel || cleanLabel === newName) {
      submitWithFeedName(newFeedName);
      return;
    }

    // Notes label doesn't match the newly-picked product → confirm.
    setPendingDivergence({
      oldNotesLabel: cleanLabel,
      newProductName: newFeedName,
    });
  };

  const resolveDivergence = (updateNotes: boolean) => {
    const newFeedName = getSelectedFeedName();
    if (updateNotes) {
      // Replace old form-suffixed label with the new product's name.
      // User notes (after |) are preserved.
      submitWithFeedName(newFeedName);
    } else {
      // Save as-is: write the new product FK + structured form; notes keep
      // only the user portion (form lives in feed_form now).
      onSubmit({
        name,
        product_id: selectedProductId || null,
        feed_form: selectedForm,
        capacity_lbs: capacity,
        current_lbs: currentLbs,
        reorder_threshold_percent: threshold,
        is_active: isActive,
        notes: userNotes,
      });
    }
    setPendingDivergence(null);
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>Edit Bulk Bin</h2>
        <div style={styles.formGroup}><label style={styles.label}>Bin Name</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} style={styles.input} placeholder="e.g., BYOC Layer" /></div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Feed Formula</label>
          <select
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value ? parseInt(e.target.value) : '')}
            style={styles.select}
            disabled={loadingFormulas}
          >
            <option value="">
              {loadingFormulas ? 'Loading formulas...' : 'Select a formula... (or leave empty)'}
            </option>
            {/* Sentinel showing the currently-linked product when it's
                not in the dropdown list. Disabled so the user must
                actively pick a real option or clear to empty. */}
            {currentNotInDropdown && (
              <option value="" disabled>
                Current: {bin.product_name} (not in mill list — pick a new one or save with no formula)
              </option>
            )}
            {apiFormulas.map(feed => (
              <option key={feed.id} value={feed.id}>{feed.name}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}><label style={styles.label}>Form Type</label><select value={selectedForm} onChange={(e) => setSelectedForm(e.target.value)} style={styles.select}>{FEED_FORMS.map(form => (<option key={form.value} value={form.value}>{form.label}</option>))}</select>{getSelectedFeedName() === 'Scratch' && selectedForm !== 'WHOLE' && <p style={styles.hint}>Note: Scratch is typically Whole Grain</p>}</div>
        <div style={styles.formRow}><div style={styles.formGroupHalf}><label style={styles.label}>Capacity (lbs)</label><input type="text" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value as any)} onBlur={(e) => { const val = parseFloat(e.target.value) || 0; setCapacity(Math.max(0, val)); }} style={styles.input} /></div><div style={styles.formGroupHalf}><label style={styles.label}>Current Fill (lbs)</label><input type="text" inputMode="numeric" value={currentLbs} onChange={(e) => setCurrentLbs(e.target.value as any)} onBlur={(e) => { const val = parseFloat(e.target.value) || 0; setCurrentLbs(Math.min(Math.max(0, val), capacity)); }} style={styles.input} /></div></div>
        <div style={styles.formGroup}><label style={styles.label}>Refill Threshold (%)</label><input type="text" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value as any)} onBlur={(e) => { const val = parseInt(e.target.value) || 0; setThreshold(Math.min(Math.max(0, val), 100)); }} style={styles.input} /></div>
        <div style={styles.formGroup}><label style={styles.checkboxLabel}><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={styles.checkbox} />Active (visible in store)</label></div>
        <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea value={userNotes} onChange={(e) => setUserNotes(e.target.value)} style={styles.textarea} placeholder="Optional notes..." /></div>
        <div style={styles.modalActions}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          {/* Only require a name. Product is optional — bin#13 (Rabbit
              Pellets, null product) and bin#14 (Cavalry Mash) need to
              be saveable without forcing a formula re-pick on every
              edit. */}
          <button
            style={styles.submitBtn}
            onClick={checkDivergenceAndSubmit}
            disabled={!name}
          >
            Save Changes
          </button>
        </div>

        {/* Notes-vs-product divergence confirmation */}
        {pendingDivergence && (
          <div style={styles.modalOverlay} onClick={() => setPendingDivergence(null)}>
            <div
              style={{ ...styles.modal, maxWidth: 420 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px 0', color: '#92400E' }}>
                ⚠ Notes don't match the new product
              </h3>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
                Notes currently say <strong>"{pendingDivergence.oldNotesLabel}"</strong> but
                the new product is <strong>"{pendingDivergence.newProductName}"</strong>.
                <br /><br />
                Update notes to match? This avoids future drift where the staff
                card and customer page disagree.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  style={{ ...styles.cancelBtn, flex: 1 }}
                  onClick={() => resolveDivergence(false)}
                >
                  Save as-is
                </button>
                <button
                  style={{ ...styles.submitBtn, flex: 1 }}
                  onClick={() => resolveDivergence(true)}
                >
                  Yes, update notes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddBinModal: React.FC<{onClose: () => void; onSubmit: (data: any) => void;}> = ({ onClose, onSubmit }) => {
  const [name, setName] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<number | ''>('');
  const [selectedForm, setSelectedForm] = useState('MASH');
  const [capacity, setCapacity] = useState(500);
  const [threshold, setThreshold] = useState(20);
  const [apiFormulas, setApiFormulas] = useState<{id: number; name: string}[]>([]);
  const [loadingFormulas, setLoadingFormulas] = useState(true);

  // Load ALL formulas (not just mill_only) — matches the EditBinModal
  // change so the user has the same options regardless of which path
  // they use to create / edit a bin.
  useEffect(() => {
    const loadFormulas = async () => {
      try {
        const { feedFormulasAPI } = await import('../../services/api');
        const { data } = await feedFormulasAPI.getAll();
        const list = data?.results || data || [];
        setApiFormulas(
          list.map((f: any) => ({ id: f.id, name: f.name }))
              .sort((a: {name: string}, b: {name: string}) => a.name.localeCompare(b.name))
        );
      } catch {
        // Formula API failed — show an empty dropdown rather than a
        // stale hardcoded list. Better to surface the load failure
        // than silently substitute outdated options. (FIVE_STAR_FEEDS
        // removed 2026-05-16; was the source of the parser-misfire bug.)
        setApiFormulas([]);
      } finally {
        setLoadingFormulas(false);
      }
    };
    loadFormulas();
  }, []);

  const getSelectedFeedName = () => {
    if (!selectedProductId) return '';
    return apiFormulas.find(f => f.id === selectedProductId)?.name || '';
  };

  const handleSubmit = () => {
    // Step 3: form goes to the structured feed_form column, not notes. Notes are
    // left blank at create (operator adds user notes later via Edit).
    onSubmit({
      name, product_id: selectedProductId || null, feed_form: selectedForm,
      capacity_lbs: capacity, reorder_threshold_percent: threshold, notes: '',
    });
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>Add Bulk Bin</h2>
        <div style={styles.formGroup}><label style={styles.label}>Bin Name</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} style={styles.input} placeholder="e.g., BYOC Layer" /></div>
        <div style={styles.formGroup}><label style={styles.label}>Feed Formula</label><select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value ? parseInt(e.target.value) : '')} style={styles.select} disabled={loadingFormulas}><option value="">{loadingFormulas ? 'Loading formulas...' : 'Select a formula... (or leave empty)'}</option>{apiFormulas.map(feed => (<option key={feed.id} value={feed.id}>{feed.name}</option>))}</select></div>
        <div style={styles.formGroup}><label style={styles.label}>Form Type</label><select value={selectedForm} onChange={(e) => setSelectedForm(e.target.value)} style={styles.select}>{FEED_FORMS.map(form => (<option key={form.value} value={form.value}>{form.label}</option>))}</select></div>
        <div style={styles.formGroup}><label style={styles.label}>Capacity (lbs)</label><input type="text" inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value as any)} onBlur={(e) => { const val = parseInt(e.target.value) || 0; setCapacity(Math.max(0, val)); }} style={styles.input} /></div>
        <div style={styles.formGroup}><label style={styles.label}>Refill Threshold (%)</label><input type="text" inputMode="numeric" value={threshold} onChange={(e) => setThreshold(e.target.value as any)} onBlur={(e) => { const val = parseInt(e.target.value) || 0; setThreshold(Math.min(Math.max(0, val), 100)); }} style={styles.input} /></div>
        {/* Same validation rule as Edit: only require a name. Formula
            is optional so the bin can be added even when the operator
            doesn't know which formula it'll hold yet. */}
        <div style={styles.modalActions}><button style={styles.cancelBtn} onClick={onClose}>Cancel</button><button style={styles.submitBtn} onClick={handleSubmit} disabled={!name}>Add Bin</button></div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f5' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '1.5rem' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.5rem', fontWeight: 'bold', color: '#1a4d1a', margin: 0 },
  addButton: { backgroundColor: '#4CAF50', color: '#fff', border: 'none', padding: '0.625rem 1.25rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  loading: { textAlign: 'center', padding: '3rem', color: '#666' },
  error: { backgroundColor: '#ffebee', color: '#c62828', padding: '1rem', borderRadius: '8px' },
  binsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' },
  binCard: { backgroundColor: '#fff', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #e0e0e0' },
  binHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' },
  binHeaderActions: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  binName: { fontSize: '1.125rem', fontWeight: 600, color: '#333', margin: 0 },
  editBtn: { background: 'none', border: '1px solid #ddd', cursor: 'pointer', fontSize: '0.75rem', padding: '0.25rem 0.5rem', borderRadius: '4px', color: '#666' },
  refillBadge: { padding: '0.25rem 0.5rem', backgroundColor: '#f44336', color: '#fff', borderRadius: '4px', fontSize: '0.625rem', fontWeight: 'bold' },
  feedDisplay: { backgroundColor: '#f0f7f0', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' },
  feedName: { fontSize: '1rem', fontWeight: 600, color: '#1a4d1a', marginBottom: '0.25rem' },
  feedDetails: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  feedForm: { fontSize: '0.8rem', color: '#666', backgroundColor: '#fff', padding: '0.125rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd' },
  feedSeasonal: { fontSize: '0.75rem', color: '#888', fontStyle: 'italic' },
  levelContainer: { marginBottom: '1rem' },
  levelBar: { height: '24px', backgroundColor: '#e0e0e0', borderRadius: '12px', overflow: 'hidden', marginBottom: '0.375rem' },
  levelFill: { height: '100%', transition: 'width 0.3s ease' },
  levelText: { textAlign: 'center', fontSize: '0.875rem', fontWeight: 500, color: '#333' },
  binStats: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' },
  stat: { fontSize: '0.8rem' },
  statLabel: { color: '#888', marginRight: '0.25rem' },
  statValue: { fontWeight: 500, color: '#333' },
  qrCode: { fontSize: '0.75rem', color: '#999', marginBottom: '1rem', fontFamily: 'monospace' },
  binActions: { display: 'flex', gap: '0.5rem' },
  fillBtn: { flex: 1, backgroundColor: '#1a4d1a', color: '#fff', border: 'none', padding: '0.625rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 },
  emptyState: { gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#666', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  modal: { backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '450px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontSize: '1.25rem', fontWeight: 'bold', color: '#333', margin: '0 0 1rem 0' },
  modalSubtitle: { color: '#666', margin: '0 0 0.5rem 0' },
  currentLevel: { fontSize: '0.875rem', color: '#888', marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' },
  newLevel: { fontSize: '0.875rem', color: '#1a4d1a', fontWeight: 500, marginTop: '0.5rem' },
  formGroup: { marginBottom: '1rem' },
  formGroupHalf: { flex: 1 },
  formRow: { display: 'flex', gap: '1rem', marginBottom: '1rem' },
  label: { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#333', marginBottom: '0.375rem' },
  input: { width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', minHeight: '44px' },
  select: { width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', boxSizing: 'border-box', backgroundColor: '#fff', minHeight: '44px' },
  textarea: { width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', minHeight: '80px', resize: 'vertical', boxSizing: 'border-box' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#333', cursor: 'pointer' },
  checkbox: { width: '24px', height: '24px', cursor: 'pointer', minWidth: '44px', minHeight: '44px' },
  hint: { fontSize: '0.75rem', color: '#ff9800', marginTop: '0.25rem' },
  modalActions: { display: 'flex', gap: '0.75rem', marginTop: '1.5rem' },
  cancelBtn: { flex: 1, padding: '0.75rem', border: '1px solid #ddd', backgroundColor: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', minHeight: '44px' },
  submitBtn: { flex: 1, padding: '0.75rem', border: 'none', backgroundColor: '#4CAF50', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 500, minHeight: '44px' },
};

export default StoreBulkBins;
